import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/system";
import useMediaQuery from "@mui/material/useMediaQuery";
import { MediaContext } from "../../context/MediaContext";
import { APP_NAME } from "../../constants/constants";

import HomeIcon from "@mui/icons-material/Home";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import InfoIcon from "@mui/icons-material/Info";
import LogoutIcon from "@mui/icons-material/Logout";

const backgroundColor = "#212529";
const textColor = "#D3D3D3";
const activeItemColor = "#3f51b5";
const inactiveItemColor = "#D3D3D3";

const NavBar = ({ onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { releaseMedia } = useContext(MediaContext);
  const location = useLocation();
  const theme = useTheme();

  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("lg", "xl"));

  const drawerWidth =
    isVerySmallScreen || isSmallScreen ? 220 : isMediumScreen ? 260 : 300;

  const handleLogout = async () => {
    await releaseMedia();
    onLogout();
  };

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const isActive = (link) => link && location.pathname.startsWith(link);

  const navItems = [
    { name: "Home", link: "/dashboard", Icon: HomeIcon },
    { name: "Profile", link: "/profile", Icon: PersonOutlineIcon },
    { name: "About", link: "/about", Icon: InfoIcon },
    { name: "Logout", callback: handleLogout, Icon: LogoutIcon },
  ];

  const drawer = (
    <Box
      onClick={handleDrawerToggle}
      sx={{
        textAlign: "center",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor,
      }}
    >
      <Box sx={{ py: 2 }}>
        <Typography
          variant="h6"
          sx={{
            color: activeItemColor,
            fontSize: "2rem",
            fontWeight: 700,
            fontFamily: "'Chalkboard SE', 'Comic Sans MS', cursive",
          }}
        >
          <Link
            to="/dashboard"
            style={{ textDecoration: "none", color: theme.palette.primary }}
          >
            {APP_NAME}
          </Link>
        </Typography>
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

      <List sx={{ mt: 1 }}>
        {navItems.map((item) => {
          const active = isActive(item.link);
          const isLogout = item.name === "Logout";

          const content = (
            <Typography
              variant="body1"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                fontWeight: active && !isLogout ? 600 : 500,
                color: isLogout
                  ? theme.palette.error.main
                  : active
                  ? activeItemColor
                  : inactiveItemColor,
              }}
            >
              <item.Icon
                sx={{
                  fontSize: 22,
                  color: isLogout
                    ? theme.palette.error.main
                    : active
                    ? activeItemColor
                    : inactiveItemColor,
                }}
              />
              {item.name}
            </Typography>
          );

          return (
            <ListItem key={item.name} disablePadding>
              <ListItemButton
                sx={{
                  textAlign: "center",
                  width: "100%",
                  borderRadius: 2,
                  mx: 2,
                  my: 0.5,
                  py: 1,
                  transition: "all 0.18s ease-out",
                  backgroundColor: active
                    ? "rgba(124,77,255,0.16)"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.06)",
                    transform: "translateY(-1px)",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.callback) item.callback();
                }}
              >
                {item.link ? (
                  <Link
                    to={item.link}
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      textDecoration: "none",
                    }}
                  >
                    {content}
                  </Link>
                ) : (
                  content
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        component="nav"
        sx={{
          backgroundColor: backgroundColor,
          backgroundImage: "none",
          boxShadow: "none",
          height: "64px",
        }}
      >
        <Toolbar sx={{ maxWidth: 1200, width: "100%", mx: "auto" }}>
          {/* Mobile Menu Button */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Brand Name */}
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              color: theme.palette.primary,
              fontSize: "1.8rem",
              fontWeight: "bold",
              fontFamily: "'Chalkboard SE', 'Comic Sans MS', cursive",
            }}
          >
            <Link
              to="/dashboard"
              style={{ textDecoration: "none", color: theme.palette.primary }}
            >
              {APP_NAME}
            </Link>
          </Typography>

          {/* Desktop Nav */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1 }}>
            {navItems.map((item) => {
              const active = isActive(item.link);
              const isLogout = item.name === "Logout";

              const content = (
                <>
                  <item.Icon
                    sx={{
                      fontSize: 20,
                      mr: 0.7,
                      color: isLogout
                        ? theme.palette.error.main
                        : active
                        ? activeItemColor
                        : inactiveItemColor,
                    }}
                  />
                  {item.name}
                </>
              );

              return (
                <Button
                  key={item.name}
                  onClick={() => item.callback && item.callback()}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.95rem",
                    fontWeight: active ? 700 : 500,
                    borderRadius: 999,
                    px: 2,
                    py: 0.7,
                    backgroundColor: active
                      ? "rgba(124,77,255,0.16)"
                      : "transparent",
                    color: isLogout
                      ? theme.palette.error.main
                      : active
                      ? activeItemColor
                      : textColor,
                    transition: "all 0.18s ease-out",
                    "&:hover": {
                      backgroundColor: isLogout
                        ? "rgba(239,68,68,0.12)"
                        : "rgba(255,255,255,0.06)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {item.link ? (
                    <Link
                      to={item.link}
                      style={{
                        textDecoration: "none",
                        color: "inherit",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {content}
                    </Link>
                  ) : (
                    content
                  )}
                </Button>
              );
            })}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            backgroundColor: backgroundColor,
            borderRight: "none",
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

NavBar.propTypes = {
  onLogout: PropTypes.func.isRequired,
};

export default NavBar;
